import { useMemo } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { useFigures } from "@/hooks/use-figures";
import { useGenerateReview } from "@/hooks/use-generate";
import { useTerms } from "@/hooks/use-terms";
import { renamesOf } from "@/lib/old-names";
import {
  findStage,
  stagePath,
  subStepPath,
  tabPath,
  type V2StageId,
} from "../../stage-vocabulary";
import { GenerateConfirm } from "./GenerateConfirm";
import type { EditTarget } from "./ReviewCard";
import { ClaimsCard } from "./ReviewClaims";
import { TitleCard } from "./ReviewHeadCards";
import { QuestionsCard } from "./ReviewQuestions";
import { ChangesCard } from "./ReviewChanges";
import { TermsCard } from "./ReviewTerms";
import {
  ClassificationCard,
  FiguresCard,
  FrameworkCard,
  InventionCard,
  NoveltyCard,
} from "./ReviewRecords";
import { ReadProblem } from "@/components/patterns/ReadProblem";

function stage(taskId: string, id: V2StageId): EditTarget {
  return { label: findStage(id)?.label ?? id, to: stagePath(taskId, id) };
}

/**
 * Each figure's drawing, and its own words (the brief description and the
 * walk-through), by its file, as the Figures stage serves them; the old names a
 * figure's words may still use, from the same read the terms card makes; and
 * both reads as they are, for the slide's lock.
 */
function useFigureParts(taskId: string) {
  const figures = useFigures(taskId);
  const parts = useMemo(
    () => ({
      thumbnails: new Map(
        (figures.data?.figures ?? []).map((f) => [f.file_id, f.thumbnail_url]),
      ),
      words: new Map(
        (figures.data?.definitions?.figures ?? []).map((d) => [
          d.file_id,
          `${d.description} ${d.walkthrough}`,
        ]),
      ),
    }),
    [figures.data],
  );
  const terms = useTerms(taskId, true).data;
  return {
    ...parts,
    renames: renamesOf(terms),
    // The figures' words, not their drawings' addresses, which are signed anew on each read.
    reads: [figures.data?.definitions, terms] as const,
  };
}

/** Everything the run would read, card by card, then the confirmation that comes before Generate. */
export function ReviewPage({ taskId }: { taskId: string }) {
  const review = useGenerateReview(taskId);
  const { thumbnails, words, renames, reads } = useFigureParts(taskId);
  // A failed read again keeps the review it had, with its lock and Generate.
  const problem = <ReadProblem query={review} what="the review" />;
  const data = review.data;
  if (data === undefined) {
    return review.isError ? (
      problem
    ) : (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner size="sm" />
        Reading what you have settled…
      </p>
    );
  }
  return (
    <>
      {problem}
      <ChangesCard taskId={taskId} />
      {data.open_questions.length > 0 && (
        <QuestionsCard taskId={taskId} count={data.open_questions.length} />
      )}
      <ClassificationCard
        codes={data.classification}
        edit={{
          label: "Classification",
          to: subStepPath(taskId, "intake", "classification"),
        }}
      />
      <TitleCard
        key={data.invention_title ?? ""}
        taskId={taskId}
        title={data.invention_title}
        proposed={data.proposed_title}
      />
      <InventionCard
        elements={data.elements}
        edit={{
          label: "Invention",
          to: subStepPath(taskId, "thesis", "invention"),
        }}
      />
      <NoveltyCard
        positions={data.positions}
        edit={{
          label: "Novelty",
          to: tabPath(taskId, "thesis", "novelty", "analysis"),
        }}
      />
      <ClaimsCard
        taskId={taskId}
        claims={data.claims}
        edit={stage(taskId, "claim-strategy")}
      />
      <FiguresCard
        figures={data.figures}
        thumbnails={thumbnails}
        words={words}
        renames={renames}
        edit={stage(taskId, "figures")}
      />
      <FrameworkCard
        sections={data.sections}
        edit={stage(taskId, "planning-outline")}
      />
      <TermsCard taskId={taskId} />
      <GenerateConfirm
        taskId={taskId}
        ready={data.ready}
        gates={data.gates}
        openItemsAddress={data.open_items_address}
        shown={[data, ...reads]}
      />
    </>
  );
}
